import { memo } from "react";
import type { DragLayerMonitor } from "react-dnd";
import { useDragLayer } from "react-dnd";
import type {
  DraggableFieldType,
  DraggableMetaType,
  FieldWrapper,
  ReportField,
  SortField,
} from "../../../../../../../../api/types/svTypes.ts";
import { AreaItemType } from "../../../../../../../../api/types/svTypes.ts";
import { isCustomColumn, isDimensionBased, isMeasure, isMetric } from "../../../../utilities/fieldsHelper.ts";
import SortFieldItem from "../../tabular/table/SortFieldItem.tsx";
import { extractCaptionFromFieldWrapper } from "../utilities/dropFieldContainerHelper.ts";
import AreaFieldItemDraggable from "./AreaFieldItemDraggable.tsx";
import { DraggingBox } from "./DraggingBox.tsx";
import FieldItemDraggable from "./FieldItemDraggable.tsx";

function CustomDragLayerInternal() {
  const { currentOffset, item, itemType } = useDragLayer(
    (monitor: DragLayerMonitor<FieldWrapper<DraggableFieldType> | DraggableMetaType>) => {
      return {
        isDragging: monitor.isDragging(),
        currentOffset: monitor.getSourceClientOffset(),
        item: monitor.getItem(),
        itemType: monitor.getItemType(),
      };
    }
  );

  const caption = extractCaptionFromFieldWrapper(item);

  if (
    (itemType === AreaItemType.DIMENSION ||
      itemType === AreaItemType.MEASURE ||
      itemType === AreaItemType.METRIC ||
      itemType === AreaItemType.AGGREGATION) &&
    currentOffset
  ) {
    return (
      <DraggingBox sx={{ transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)` }}>
        <FieldItemDraggable field={item} caption={caption} />
      </DraggingBox>
    );
  }

  if (
    (itemType === AreaItemType.CONDITIONS ||
      itemType === AreaItemType.ROWS ||
      itemType === AreaItemType.COLUMNS ||
      itemType === AreaItemType.ARGUMENTS ||
      itemType === AreaItemType.VALUES) &&
    currentOffset
  ) {
    return (
      <DraggingBox sx={{ transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)` }}>
        <AreaFieldItemDraggable key={item.field.guid} caption={caption} />
      </DraggingBox>
    );
  }

  if (itemType === AreaItemType.FIELDS && currentOffset) {
    const tableField = item as FieldWrapper<ReportField>;
    if (
      isDimensionBased(tableField.field) ||
      isMeasure(tableField.field) ||
      isMetric(tableField.field) ||
      isCustomColumn(tableField.field)
    ) {
      return (
        <DraggingBox sx={{ transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)` }}>
          <AreaFieldItemDraggable key={tableField.field.config.guid} caption={caption} />
        </DraggingBox>
      );
    }
  }

  if (itemType === AreaItemType.SORTS && currentOffset) {
    const field = item as SortField;

    return (
      <DraggingBox sx={{ transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)`, width: "323px" }}>
        <SortFieldItem key={field.config.name} sortField={field} />
      </DraggingBox>
    );
  }

  return null;
}

const CustomDragLayer = memo(CustomDragLayerInternal, () => true);

export default CustomDragLayer;
